import type { LocalResourcesSnapshot } from "../types.ts";
import type { LogEntry } from "../types.ts";

export function fmtBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function tsLabel(ms: number): string {
  try {
    return new Date(ms).toLocaleTimeString();
  } catch {
    return "";
  }
}

function loadedNames(snapshot: LocalResourcesSnapshot): string[] {
  return snapshot.ollama.loadedModels.map((m) => m.name).sort();
}

export function diffLoadedModelLines(
  previous: LocalResourcesSnapshot | null,
  next: LocalResourcesSnapshot,
): string[] {
  if (!previous) {
    return [];
  }
  const prevSet = new Set(loadedNames(previous));
  const nextSet = new Set(loadedNames(next));
  const lines: string[] = [];
  for (const name of nextSet) {
    if (!prevSet.has(name)) {
      lines.push(`loaded model appeared: ${name}`);
    }
  }
  for (const name of prevSet) {
    if (!nextSet.has(name)) {
      lines.push(`loaded model unloaded: ${name}`);
    }
  }
  return lines;
}

export function buildActivityLines(params: {
  snapshot: LocalResourcesSnapshot;
  previous: LocalResourcesSnapshot | null;
  selectedModel: string | null;
}): string[] {
  const { snapshot, previous, selectedModel } = params;
  const stamp = tsLabel(snapshot.collectedAtMs);
  const ollama = snapshot.ollama;
  const gateway = snapshot.gateway;
  const lines: string[] = [];

  if (!ollama.reachable) {
    lines.push(
      `[${stamp}] Ollama unreachable at ${ollama.baseUrl}${ollama.error ? ` (${ollama.error})` : ""}`,
    );
    return lines;
  }

  const installed = selectedModel
    ? ollama.installedModels.filter((m) => m.name === selectedModel)
    : ollama.installedModels;
  const loaded = selectedModel
    ? ollama.loadedModels.filter((m) => m.name === selectedModel)
    : ollama.loadedModels;

  lines.push(
    `[${stamp}] probe OK · Ollama ${ollama.version ?? "?"} · ${installed.length} installed · ${loaded.length} loaded in RAM`,
  );

  for (const line of diffLoadedModelLines(previous, snapshot)) {
    lines.push(`[${stamp}] ${line}`);
  }

  for (const model of loaded) {
    const vram = model.sizeVram != null ? fmtBytes(model.sizeVram) : "—";
    lines.push(
      `[${stamp}] RAM ${model.name} · size ${fmtBytes(model.size)} · VRAM ${vram}${model.expiresAt ? ` · expires ${model.expiresAt}` : ""}`,
    );
  }

  if (!selectedModel && loaded.length === 0 && ollama.reachable) {
    lines.push(`[${stamp}] no models loaded in Ollama (idle or cold)`);
  }

  lines.push(
    `[${stamp}] gateway agent runs ${gateway.activeRuns}/${gateway.mainMaxConcurrent} · streaming ${gateway.streamingRuns} (${gateway.mainSlotsFree} main slots free)`,
  );

  const ollamaInflight = gateway.inflightRuns.filter(
    (run) => run.provider.trim().toLowerCase() === "ollama",
  );
  for (const run of ollamaInflight) {
    const sessionLabel = run.sessionKey?.trim() || run.sessionId;
    lines.push(
      `[${stamp}] inflight ${run.modelId} · session ${sessionLabel} · ${run.streaming ? "streaming" : "active"}`,
    );
  }

  if (ollama.error) {
    lines.push(`[${stamp}] Ollama partial error: ${ollama.error}`);
  }

  return lines;
}

export function formatGatewayLogLine(entry: LogEntry): string {
  const time = entry.time ? new Date(entry.time).toLocaleTimeString() : "";
  const level = entry.level ? entry.level.toUpperCase() : "LOG";
  const message = entry.message?.trim() || entry.raw;
  return `[${time}] [${level}] ${message}`;
}

export function appendConsoleLines(existing: string[], additions: string[], max = 500): string[] {
  if (additions.length === 0) {
    return existing;
  }
  return [...existing, ...additions].slice(-max);
}
